'use client'

import { type FulfillmentApi } from '@entities/fulfillment'
import { Field, FieldDescription, FieldError, FieldLabel, FieldPanel } from '@ui/base/field'
import { Textarea } from '@ui/base/textarea'
import { useFormContext } from 'react-hook-form'

import { type FulfillmentFormValues } from '../model/types'
import { CoverField } from './cover-field'
import { FormBlock } from './form-block'
import { PhotosField } from './photos-field'
import { RequiredMark } from './required-mark'

type Props = {
  cover: File | null
  slug?: string
  hasExistingCover: boolean
  coverError: string
  onSelectCover: (file: File | undefined) => void
  savedPhotos: FulfillmentApi.Photo[]
  photoFiles: File[]
  photoError: string
  onChoosePhotos: (files: File[]) => void
  onRemoveSavedPhoto: (photoId: string) => void
}

/** Шестой шаг: текст карточки и снимки склада. */
export const PresentationStep = ({
  cover,
  slug,
  hasExistingCover,
  coverError,
  onSelectCover,
  savedPhotos,
  photoFiles,
  photoError,
  onChoosePhotos,
  onRemoveSavedPhoto,
}: Props) => {
  const {
    register,
    trigger,
    formState: { errors },
  } = useFormContext<FulfillmentFormValues>()
  const descriptionField = register('description')

  return (
    <>
      <FormBlock
        title="О фулфилменте"
        description="Главный текст карточки: чем занимаетесь, что берёте в работу и чем отличаетесь от соседей по каталогу."
      >
        <Field data-invalid={Boolean(errors.description?.message)}>
          <FieldLabel htmlFor="fulfillment-description">
            Описание <RequiredMark />
          </FieldLabel>
          {/* Высота задана классом: поле растёт по содержимому, и rows на него не влияет. */}
          <Textarea
            id="fulfillment-description"
            className="min-h-72"
            aria-invalid={Boolean(errors.description?.message)}
            aria-describedby={
              errors.description?.message ? 'fulfillment-description-error' : 'fulfillment-description-hint'
            }
            {...descriptionField}
            onChange={async (event) => {
              await descriptionField.onChange(event)
              await trigger('description')
            }}
          />
          <FieldError id="fulfillment-description-error">{errors.description?.message}</FieldError>
          <FieldDescription id="fulfillment-description-hint">
            От 30 до 5000 символов. Продавцы читают его первым, когда открывают карточку.
          </FieldDescription>
        </Field>
      </FormBlock>

      <FormBlock title="Фотографии" description="Главное фото видно в каталоге, остальные — в галерее карточки.">
        {/* Два поля встают рядом только на широком экране: в каждом из них ещё и список выбранных
            файлов, которому в половине ширины тесно. Выравниваем по верху — колонки разной высоты. */}
        <div className="grid items-start gap-4 lg:grid-cols-2">
          {/* Каждая зона на своей подложке: две пунктирные рамки подряд на белом сливались бы
              в одну область, и было бы неясно, куда какой файл кладут. */}
          <FieldPanel>
            <CoverField
              file={cover}
              slug={slug}
              hasExistingCover={hasExistingCover}
              error={coverError}
              onSelect={onSelectCover}
            />
          </FieldPanel>
          <FieldPanel>
            <PhotosField
              savedPhotos={savedPhotos}
              files={photoFiles}
              error={photoError}
              onChange={onChoosePhotos}
              onRemoveSaved={onRemoveSavedPhoto}
            />
          </FieldPanel>
        </div>
      </FormBlock>
    </>
  )
}
